import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Info, Search, Send } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import MessageText from '@/client/components/admin/MessageText/MessageText';
import {
  InboxChannel,
  InboxConversation,
  inboxKeys,
  useInboxConversationsQuery,
  useInboxReadMutation,
  useInboxReplyMutation,
  useInboxThreadQuery,
  useMailboxesQuery,
} from '@/client/api/inbox/inbox';
import { smsLineLabel, useSmsLinesQuery } from '@/client/api/inbox/smsLines';
import EmailEditor from '@/client/components/admin/Email/EmailEditor';
import { SignatureToggle } from '@/client/components/admin/Email/Signatures';

const CHANNELS: { id: InboxChannel; label: string; style: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp', style: 'bg-green-50 text-green-700 border-green-200' },
  { id: 'sms', label: 'SMS', style: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'email', label: 'Email', style: 'bg-purple-50 text-purple-700 border-purple-200' },
];
const channelOf = (id: string) => CHANNELS.find((c) => c.id === id);

/** A phone number gets its +; an email address is shown as it is. */
const addressOf = (c: { channel: InboxChannel; peer: string }) => (c.channel === 'email' ? c.peer : `+${c.peer}`);
const keyOf = (c: { channel: InboxChannel; peer: string }) => `${c.channel}:${c.peer}`;

const messageTime = (at: number) => {
  const when = moment.unix(at);
  return when.isSame(moment(), 'day') ? when.format('HH:mm') : when.format('D MMM, HH:mm');
};

/**
 * Every conversation FeelyFeely is having with a person, on any channel.
 *
 * Two filters: the channel, then what sits under it: the mailbox written to
 * for email, the SMS line for SMS. WhatsApp conversations do not record which
 * of our numbers they came in on, so they get no second select rather than
 * one that would hide most of the list.
 *
 * `?channel=sms` (or `email`, `whatsapp`), `?mailbox=` and `?line=` open it filtered,
 * which is how the per-channel menu entries reach the same page. A legacy
 * comma list keeps its first channel.
 */
const InboxPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const channel = ((params.get('channel') || '').split(',').find((c) => channelOf(c)) || '') as InboxChannel | '';
  const filter: InboxChannel[] = channel ? [channel] : [];
  const mailboxes = useMailboxesQuery().data || [];
  // A mailbox only narrows email, so it is dropped whenever the channel changes.
  const mailbox = channel === 'email' ? params.get('mailbox') || '' : '';
  const setChannel = (next: InboxChannel | '') => setParams(next ? { channel: next } : {}, { replace: true });
  const setMailbox = (next: string) =>
    setParams(next ? { channel: 'email', mailbox: next } : { channel: 'email' }, { replace: true });
  const smsLines = (useSmsLinesQuery().data?.lines || []).filter((l) => l.enabled);
  const line = channel === 'sms' ? params.get('line') || '' : '';
  const setLine = (next: string) => setParams(next ? { channel: 'sms', line: next } : { channel: 'sms' }, { replace: true });

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<InboxConversation | null>(null);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  // Email replies are written in the rich editor; `body` holds its plain text.
  const [html, setHtml] = useState('');
  const [draftKey, setDraftKey] = useState(0);
  const [from, setFrom] = useState('');
  const [withSignature, setWithSignature] = useState(true);
  const endRef = useRef<HTMLDivElement | null>(null);

  const list = useInboxConversationsQuery({
    ...(filter.length ? { channel: filter.join(',') } : {}),
    ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
    ...(mailbox ? { mailbox } : {}),
    ...(line ? { line } : {}),
  });
  const conversations = list.data?.conversations || [];
  const thread = useInboxThreadQuery(selected?.channel ?? null, selected?.peer ?? null);
  const messages = thread.data?.messages || [];
  const reply = useInboxReplyMutation();
  const { mutate: markRead } = useInboxReadMutation();

  const open = (conversation: InboxConversation) => {
    // WhatsApp has its own inbox: templates, media and the 24-hour rule live there.
    if (conversation.channel === 'whatsapp') {
      navigate(`/admin/relationship/whatsapp/conversations?phone=${conversation.peer}`);
      return;
    }
    setSelected(conversation);
    setBody('');
    setHtml('');
    setDraftKey((k) => k + 1);
    // Answer from the address or line they wrote to. An SMS conversation with
    // no line yet leaves on the default line (empty = let the server pick).
    setFrom((conversation.channel === 'sms' ? conversation.line : conversation.mailbox) || '');
    // An email reply continues the subject it answers.
    const last = conversation.last_message_preview || '';
    setSubject(conversation.channel === 'email' && last ? (/^re:/i.test(last) ? last : `Re: ${last}`) : '');
    markRead(
      { channel: conversation.channel, peer: conversation.peer },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox', 'list'] }) },
    );
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, selected]);

  const send = () => {
    if (!selected || !body.trim() || reply.isPending) return;
    reply.mutate(
      {
        channel: selected.channel,
        peer: selected.peer,
        body,
        ...(selected.channel === 'email' && subject.trim() ? { subject: subject.trim() } : {}),
        ...(selected.channel === 'email' && html ? { html } : {}),
        ...(selected.channel === 'email' && from ? { from, signature: withSignature } : {}),
        ...(selected.channel === 'sms' && from ? { from } : {}),
      },
      {
        onSuccess: () => {
          setBody('');
          setHtml('');
          setDraftKey((k) => k + 1);
          queryClient.invalidateQueries({ queryKey: inboxKeys.thread(selected.channel, selected.peer) });
          queryClient.invalidateQueries({ queryKey: ['inbox', 'list'] });
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : 'The reply was not sent'),
      },
    );
  };

  const selectClass =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500';

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
        <p className="text-sm text-gray-500 mt-1">Conversations with people on WhatsApp, SMS and email</p>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, phone or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <select
                aria-label="Channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value as InboxChannel | '')}
                className={selectClass}
              >
                <option value="">All channels</option>
                {CHANNELS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              {channel === 'email' && mailboxes.length > 0 && (
                <select
                  aria-label="Mailbox"
                  value={mailbox}
                  onChange={(e) => setMailbox(e.target.value)}
                  className={selectClass}
                >
                  <option value="">All mailboxes</option>
                  {mailboxes.map((m) => (
                    <option key={m.email} value={m.email}>
                      {m.email}
                    </option>
                  ))}
                </select>
              )}
              {channel === 'sms' && smsLines.some((l) => l.two_way) && (
                <select aria-label="SMS line" value={line} onChange={(e) => setLine(e.target.value)} className={selectClass}>
                  <option value="">All lines</option>
                  {smsLines
                    .filter((l) => l.two_way)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                </select>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {list.isLoading && <p className="p-4 text-sm text-gray-500">Loading conversations...</p>}
            {list.isError && <p className="p-4 text-sm text-red-600">Could not load the inbox.</p>}
            {!list.isLoading && !list.isError && conversations.length === 0 && (
              <p className="p-4 text-sm text-gray-500">No conversations yet.</p>
            )}
            {conversations.map((conversation) => (
              <button
                key={keyOf(conversation)}
                onClick={() => open(conversation)}
                className={`w-full text-left p-4 border-b hover:bg-gray-50 transition-colors ${
                  selected && keyOf(selected) === keyOf(conversation) ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 truncate">{conversation.name || addressOf(conversation)}</h3>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {conversation.last_message_at ? moment.unix(conversation.last_message_at).fromNow() : ''}
                  </span>
                </div>
                {conversation.name && <p className="text-xs text-gray-500 truncate mb-1">{addressOf(conversation)}</p>}
                <p className="text-sm text-gray-600 truncate">{conversation.last_message_preview || ''}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${channelOf(conversation.channel)?.style}`}>
                    {channelOf(conversation.channel)?.label}
                  </span>
                  {conversation.unread_count > 0 && (
                    <span className="min-w-5 h-5 px-1 bg-green-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
                      {conversation.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-gray-50">
          {selected ? (
            <>
              <div className="bg-white border-b px-6 py-4">
                <h2 className="font-semibold text-gray-900">
                  {selected.name || addressOf(selected)}
                  {selected.name && <span className="ml-2 text-sm font-normal text-gray-500">{addressOf(selected)}</span>}
                </h2>
                <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${channelOf(selected.channel)?.style}`}>
                  {channelOf(selected.channel)?.label}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 && !thread.isLoading && (
                  <p className="text-sm text-gray-500 text-center">No messages yet</p>
                )}
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-md px-4 py-2 rounded-lg ${
                        message.direction === 'outbound' ? 'bg-green-500 text-white' : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      {message.our_address && (
                        <p className={`text-xs mb-1 ${message.direction === 'outbound' ? 'text-white/70' : 'text-gray-500'}`}>
                          {message.direction === 'outbound' ? 'From' : 'To'} {message.our_address}
                        </p>
                      )}
                      {message.subject && <p className="text-sm font-semibold mb-1">{message.subject}</p>}
                      <MessageText text={message.body} markup={false} />
                      <p className={`text-xs mt-1 text-right ${message.direction === 'outbound' ? 'text-white/70' : 'text-gray-500'}`}>
                        {messageTime(message.at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              <div className="bg-white border-t px-6 py-4 space-y-3">
                {selected.channel === 'email' && (
                  <select
                    aria-label="Reply from"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    {mailboxes.map((m) => (
                      <option key={m.email} value={m.email}>
                        From {m.name} &lt;{m.email}&gt;
                      </option>
                    ))}
                    <option value="">From FeelyFeely updates (replies are not collected)</option>
                  </select>
                )}
                {selected.channel === 'sms' && smsLines.length > 0 && (
                  <select
                    aria-label="Reply from"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">From the default line</option>
                    {smsLines.map((l) => (
                      <option key={l.id} value={l.id}>
                        From {smsLineLabel(l)}
                      </option>
                    ))}
                  </select>
                )}
                {selected.channel === 'email' && from && (
                  <SignatureToggle mailbox={mailboxes.find((m) => m.email === from)} on={withSignature} onChange={setWithSignature} preview={false} />
                )}
                {selected.channel === 'email' && (
                  <input
                    type="text"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
                <div className="flex items-end gap-3">
                  {selected.channel === 'email' ? (
                    <div className="flex-1">
                      <EmailEditor
                        key={draftKey}
                        value=""
                        minHeight={120}
                        placeholder="Write your reply..."
                        onChange={(c) => {
                          setBody(c.text);
                          setHtml(c.html);
                        }}
                      />
                    </div>
                  ) : (
                  <textarea
                    rows={2}
                    placeholder={`Reply by ${channelOf(selected.channel)?.label}...`}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  )}
                  <button
                    onClick={send}
                    disabled={reply.isPending || !body.trim()}
                    aria-label="Send reply"
                    className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Info className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversation selected</h3>
                <p className="text-sm text-gray-500">Select a conversation to read and reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboxPage;
